import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ConfirmationService, SelectItem } from '@openng/optimus-ui/api';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  REGIONS_CONTAINER,
  REGION_DETAIL_CONTAINER,
} from 'src/app/constants/common.constants';
import {
  REGION_TAB_PREFIX,
  NEW_REGION,
} from 'src/app/constants/location-constants';
import { TabService } from 'src/app/core/services/tab.service';
import { BreadCrumb } from 'src/app/model/common/bread-crumb';
import { ContactList } from 'src/app/model/contacts/contact-list';
import { RegionApiService } from 'src/app/services/api/region-api.service';
import { dynamicSort } from 'src/app/core/functions/common-functions';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { ContactListApiService } from 'src/app/services/api/contact-list-api.service';
import { BaseContainer } from 'src/app/core/containers/base-container';
import { ContactApiService } from 'src/app/services/api/contact-api.service';
import { ContactBuilderService } from 'src/app/services/builder/contact-builder.service';
import { Contact } from 'src/app/model/contacts/contact';
import { Region } from 'src/app/model/locations/region';
import { RegionBuilderService } from 'src/app/services/builder/region-builder.service';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { SelectChangeEvent, Select } from '@openng/optimus-ui/select';
import {
  AutoCompleteCompleteEvent,
  AutoComplete,
} from '@openng/optimus-ui/autocomplete';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { ScrollPanel } from '@openng/optimus-ui/scrollpanel';
import { InputText } from '@openng/optimus-ui/inputtext';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from '@openng/optimus-ui/tabs';
import { Ripple } from '@openng/optimus-ui/ripple';
import { ContactListSimpleViewContainerComponent } from '../../../contacts/containers/contact-list-simple-view/contact-list-simple-view-container.component';
import { ContactListSearchContainerComponent } from '../../../contacts/containers/contact-list-search/contact-list-search-container.component';
import { Panel } from '@openng/optimus-ui/panel';
import { Dialog } from '@openng/optimus-ui/dialog';
import { ContactListCreateContainerComponent } from '../../../contacts/containers/contact-list-create/contact-list-create-container.component';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';

@Component({
  selector: 'app-region-details-container',
  templateUrl: './region-details-container.component.html',
  styleUrls: ['./region-details-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProgressSpinnerComponent,
    BlockUI,
    FormsModule,
    ReactiveFormsModule,
    Select,
    ButtonDirective,
    ScrollPanel,
    InputText,
    AutoComplete,
    Tabs,
    TabList,
    Ripple,
    Tab,
    TabPanels,
    TabPanel,
    ContactListSimpleViewContainerComponent,
    ContactListSearchContainerComponent,
    Panel,
    Dialog,
    ContactListCreateContainerComponent,
    TranslatePipe,
    BlockableDiv,
  ],
})
export class RegionDetailsContainerComponent
  extends BaseContainer
  implements OnInit, OnDestroy
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _fb = inject(UntypedFormBuilder);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _confirmationService = inject(ConfirmationService);
  protected _contactListApi = inject(ContactListApiService);
  protected _authApi = inject(AuthApiService);
  protected _regionApi = inject(RegionApiService);
  protected _regionBuilder = inject(RegionBuilderService);
  protected _contactApi = inject(ContactApiService);
  protected _contactBuilder = inject(ContactBuilderService);
  protected _tabService = inject(TabService);

  private lId: string = '';
  @Input() set id(id) {
    this.lId = id;
  }
  get id() {
    return this.lId;
  }

  readonly index = input<number>();

  readonly changesEvent = output<any>();

  regionBreadCrumb?: BreadCrumb;
  backToSearchResult?: BreadCrumb;
  availableRegions: SelectItem[] = [];
  canCreate = false;
  canEdit = false;
  canDelete = false;
  canCreateContactList = false;
  tabClosingSub?: Subscription;
  breadCrumbSub?: Subscription;
  valueSub?: Subscription;
  region!: Region;
  loading = true;
  saving = false;
  form!: UntypedFormGroup;
  contactList: ContactList | null = null;
  createContactListDialog = false;
  displayDialog = false;
  tempContactList: ContactList | null = null;
  actions: SelectItem[] = [];
  selectedAction: any = null;
  isEditing = false;
  tabIndex = 0;
  allContacts: Contact[] = [];
  filteredContacts: SelectItem[] = [];

  constructor() {
    super();
    this.buildForm();
  }

  ngOnInit() {
    this.setupTabClosingSubscription();
    this.setupBreadCrumbSub();

    this.initialize();
  }

  setupBreadCrumbSub(): void {
    this.breadCrumbSub =
      this._breadCrumbService.breadCrumbSelectedEvent.subscribe(
        (breadCrumb) => {
          this._breadCrumbService.removeBreadCrumb(this.regionBreadCrumb!);
          const tab = this._tabService.buildNewTab(REGIONS_CONTAINER, true);
          this._tabService.setMobileTab(tab);
        }
      );
  }

  initialize() {
    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.REGIONS.CREATE_REGIONS,
    ]);
    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.REGIONS.EDIT_REGIONS,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.LOCATIONS.REGIONS.DELETE_REGIONS,
    ]);
    this.canCreateContactList = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTACTS.CONTACT_LISTS.CREATE_CONTACT_LISTS,
    ]);
    let observables = [];

    observables.push(this._contactApi.getContacts());
    if (this.id) {
      observables.push(this._regionApi.getRegion(this.id));
    } else {
      // if there's no id, we need an empty region as our base
      this.region = {
        id: '',
        name: null,
        contactListId: null,
        owner: null,
        ownerId: null,
        areas: [],
      };
    }

    forkJoin(observables)
      .pipe(take(1))
      .subscribe((responses: any) => {
        const contactData = [...responses[0]];
        this.allContacts = contactData
          .map((c) => this._contactBuilder.buildContact(c)!)
          .sort(dynamicSort('firstName', 1));

        if (responses.length > 1) {
          this.region = responses[1];
        }

        this.setupScreen();
      });
  }

  async setupScreen() {
    this.findContactList();
    this.updateForm();

    if (this._deviceService.isMobile()) {
      this._translateService
        .get('LOCATION.SCREEN.REGION')
        .subscribe((label) => {
          this._breadCrumbService.resetAndAddBreadCrumb(
            new BreadCrumb(label, null, true)
          );
          if (this.region) {
            this.regionBreadCrumb = new BreadCrumb(
              this.region.name!,
              null,
              false
            );
          }
          this._breadCrumbService.addBreadCrumb(this.regionBreadCrumb!);
        });
    } else {
      const labels = await firstValueFrom(
        forkJoin({
          new: this._translateService.get('LOCATION.LABEL.NEW_REGION'),
          regularPrefix: this._translateService.get('LOCATION.SCREEN.REGION'),
        })
      );
      if (!this.region.id) {
        this._tabService.updateActiveTabLabel(labels.new);
      } else {
        const tab = this._tabService.getTabById(this.region.id);
        this._tabService.updateTabLabel(
          tab!,
          labels.regularPrefix + ': ' + this.region.name
        );
      }
    }

    this.updateActionsList();
    this.loading = false;
  }

  findContactList() {
    if (this.region.contactListId) {
      this._contactListApi.getContactList(this.region.contactListId).subscribe({
        next: (list) => {
          this.contactList = list;
        },
        error: (error) => {
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'COMMON.MESSAGES.HEADERS.RETRIEVE_ERROR',
            bodyKey: 'CONTACT.MESSAGES.ERROR.RETRIEVE_CONTACT_LIST',
          });
        },
      });
    }
  }

  findOwner() {
    if (this.region.ownerId) {
      return this.allContacts
        .filter((c) => c.id == this.region.ownerId)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }))
        .shift();
    }
    return null;
  }

  buildForm() {
    this.form = this._fb.group({
      id: null,
      name: [null, Validators.required],
      contactListId: null,
      owner: null,
      areas: null,
      updater: [{ value: null, disabled: true }],
      version: null,
    });

    this.valueSub = this.form.valueChanges.subscribe((changes) => {
      if (this.form.dirty) {
        this.changesEvent.emit({
          isDirty: true,
          index: this.index(),
          id: this.id,
        });
      }
    });
  }

  updateForm() {
    if (!isNullOrUndefined(this.region)) {
      this.form.patchValue({
        id: this.region.id,
        name: this.region.name,
        contactListId: this.region.contactListId,
        owner: this.findOwner(),
        updater: this.region.updater,
        version: this.region.version,
      });

      if (!this.region.id) {
        this.isEditing = true;
      }

      if (!this.canEdit && !this.canCreate) {
        this.form.disable();
      } else if (this.region.id && this.isEditing === false) {
        this.form.disable();
      }
    }
  }

  updateActionsList() {
    if (this.canEdit || this.canDelete) {
      const selectAction$ = this._translateService.get(
        'COMMON.LABEL.SELECT_ACTION'
      );
      const removeList$ = this._translateService.get(
        'LOCATION.LABEL.BUTTONS.REMOVE_CONTACT_LIST'
      );
      const delete$ = this._translateService.get('COMMON.LABEL.BUTTONS.DELETE');

      let select,
        removeList,
        del = null;
      forkJoin([selectAction$, removeList$, delete$])
        .pipe(take(1))
        .subscribe((messages) => {
          select = messages[0];
          removeList = messages[1];
          del = messages[2];

          this.actions = [
            { label: select, value: null },
            { label: removeList, value: 'remove-list' },
          ];

          if (this.canDelete) {
            this.actions.push({ label: del, value: 'delete-region' });
          }

          // default actions here: Send to Day Crew, etc.
          this.actions = [...this.actions];
        });
    }
  }

  actionEvent($event: SelectChangeEvent, object?: any) {
    if ($event.value) {
      this.selectedAction = null;
      if ($event.value === 'remove-list') {
        this.clearContactList();
      } else if ($event.value === 'delete-region') {
        let confHeader = null,
          ok = null,
          cancel = null,
          message = null;
        const confHeader$ = this._translateService.get(
          'LOCATION.MESSAGES.HEADERS.DELETE_REGION'
        );
        const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
        const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
        const message$ = this._translateService.get(
          'LOCATION.MESSAGES.CONFIRMATION.DELETE_REGION',
          { name: this.region.name }
        );

        forkJoin([confHeader$, ok$, cancel$, message$]).subscribe(
          (messages) => {
            confHeader = messages[0];
            ok = messages[1];
            cancel = messages[2];
            message = messages[3];
          }
        );

        this._confirmationService.confirm({
          header: confHeader!,
          icon: 'fa fa-question-circle',
          acceptVisible: true,
          rejectVisible: true,
          acceptLabel: ok!,
          rejectLabel: cancel!,
          message: message!,
          accept: () => {
            this.saving = true;
            if (
              !isNullOrUndefined(this.region) &&
              !isNullOrUndefined(this.region.id)
            ) {
              this._regionApi.deleteRegion(this.region.id).subscribe({
                next: (result) => {
                  this.saving = false;
                  this.displayDialog = false;
                },
                error: (error) => {
                  console.error('Error deleting region', error);
                  this._logAndMessage.translateToErrorAlert({
                    headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                    bodyKey: 'LOCATION.MESSAGES.ERROR.DELETE_REGION',
                  });
                },
                complete: () => {
                  const index = this._tabService.getActiveIndex();
                  this._tabService.closeTab(index);
                },
              });
            }
          },
          reject: () => {},
        });
      }

      if (!isNullOrUndefined(object)) {
        object.clear(null);
      }
    }
  }

  ngOnDestroy(): void {
    if (this.breadCrumbSub) {
      this.breadCrumbSub.unsubscribe();
    }
  }

  saveRegion(fromAction = false) {
    if (this.form.valid || fromAction === true) {
      const regionForm = Object.assign({}, this.form.getRawValue());
      const region = Object.assign(
        {},
        {
          name: regionForm.name,
          ownerId: regionForm.owner?.value ?? regionForm.owner,
          contactListId: regionForm.contactListId,
        }
      );

      if (!regionForm.id) {
        this.createRegion(region);
      } else {
        this.updateRegion(region);
      }
    } else {
      this._logAndMessage.translateToErrorAlert({
        headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_REGION',
        bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_REGION_ERROR',
      });
    }
  }

  createRegion(region: any) {
    if (!this.canCreate) {
      return;
    }
    this.saving = true;

    region.updater = null;
    region.updatedAt = null;
    region.createdAt = null;
    const regionInput = Object.assign(
      {},
      {
        name: region.name,
        ownerId: region.ownerId,
        contactListId: region.contactListId,
      }
    );

    this._regionApi.createRegion(regionInput).subscribe({
      next: (region) => {
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
          headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
        });
        this.region = region;
        this.saving = false;
        this.findContactList();
        this.updateForm();
        this.form.markAsPristine();
      },
      error: (error) => {
        console.error(error);
        this.saving = false;
        this.setEditing(false);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_REGION',
          bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_REGION_ERROR',
        });
      },
      complete: () => {
        if (!this._deviceService.isMobile()) {
          const oldTab = this._tabService.getActiveTab();
          const tab = this._tabService.buildNewTab(
            REGION_DETAIL_CONTAINER,
            true,
            undefined,
            this.region.id
          );
          this._tabService.replaceTab(oldTab, tab);
          this.changesEvent.emit({
            isDirty: false,
            index: this.index(),
            id: this.id,
          });
        }
      },
    });
  }

  updateRegion(region: any) {
    if (!this.canEdit) {
      return;
    }

    this.saving = true;

    const regionInput = Object.assign(
      {},
      {
        name: region.name,
        ownerId: region.ownerId,
        contactListId: region.contactListId,
      }
    );

    // If the contactlistid is pristine, that means that we haven't
    // removed it or changed it, and so it won't come in from getDirtyValues
    // but our lambda will remove the list if we don't send it back
    // so we need to re-send it every time we make other changes
    // to the list
    if (this.form.controls['contactListId'].pristine) {
      regionInput['contactListId'] = this.form.controls['contactListId'].value;
    }

    if (!isNullOrUndefined(this.region)) {
      const update = this._regionBuilder.buildUpdateStatement(regionInput);

      this._regionApi.updateRegion(this.region.id, update).subscribe({
        next: (region) => {
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'COMMON.MESSAGES.SUCCESS.SAVED_SUCCESSFULLY',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUBMIT',
          });
          this.region = region;
          if (regionInput['contactListId']) {
            this.findContactList();
          }
          this.updateForm();
          this.form.markAsPristine();
          this.saving = false;
          this.setEditing(false);
        },
        error: (error) => {
          this.saving = false;
          this.setEditing(false);
          this._logAndMessage.translateToErrorAlert({
            headerKey: 'LOCATION.MESSAGES.HEADERS.SAVE_REGION',
            bodyKey: 'LOCATION.MESSAGES.ERROR.SAVING_REGION_ERROR',
          });
        },
        complete: () => {
          this.changesEvent.emit({
            isDirty: false,
            index: this.index(),
            id: this.id,
          });
        },
      });
    }
  }

  openCreateContactList() {
    this.createContactListDialog = true;
  }

  setTempList(list: ContactList) {
    if (this.canEdit === true) {
      this.setEditing(true);
      this.tempContactList = list;
      this.form.patchValue({
        contactListId: !isNullOrUndefined(this.tempContactList)
          ? this.tempContactList.id
          : null,
      });
      this.form.controls['contactListId'].markAsDirty();
      this.createContactListDialog = false;
    }
  }

  clearContactList() {
    if (this.canEdit === true) {
      this.setEditing(true);
      this.form.patchValue({
        contactListId: null,
      });
      this.form.controls['contactListId'].markAsDirty();
      this.region.contactListId = null;
      this.contactList = null;
      this.tempContactList = null;
    }
  }

  setEditing(editing: boolean) {
    if (this.canEdit || this.canCreate) {
      this.isEditing = editing;

      if (editing) {
        this.form.enable();
        // always disabled
        this.form.controls['updater'].disable();
      } else {
        this.form.disable();
      }
    } else {
      this.isEditing = false;
      this.form.disable();
      this._logAndMessage.translateToWarnMessage({
        bodyKey: 'COMMON.MESSAGES.WARN.ADMIN_RIGHTS',
        headerKey: 'COMMON.MESSAGES.HEADERS.AUTHORIZATION_MESSAGE',
      });
    }
  }

  tabChanged($event: any): void {
    if (!isNullOrUndefined($event)) {
      this.tabIndex = $event.index;
    }
  }

  setupTabClosingSubscription(): void {
    this.tabClosingSub = this._tabService.attemptCloseEvent.subscribe(
      (closingTab) => {
        if (!isNullOrUndefined(this.id)) {
          if (this.id === closingTab.id && (!this.form || !this.form.dirty)) {
            this._tabService.closeTab(closingTab.index);
          } else if (
            this.id === closingTab.id &&
            this.form &&
            this.form.dirty
          ) {
            const unsaved$ = this._translateService.get(
              'COMMON.MESSAGES.CONFIRMATION.UNSAVED_CHANGES'
            );
            const yes$ = this._translateService.get(
              'COMMON.LABEL.BUTTONS.LEAVE'
            );
            const no$ = this._translateService.get(
              'COMMON.LABEL.BUTTONS.CANCEL'
            );
            const header$ = this._translateService.get(
              'COMMON.LABEL.HEADERS.LEAVE_PAGE'
            );
            forkJoin(unsaved$, yes$, no$, header$).subscribe((messages) => {
              this._confirmationService.confirm({
                header: messages[3],
                icon: 'fa fa-question-circle',
                acceptVisible: true,
                rejectVisible: true,
                acceptLabel: messages[1],
                rejectLabel: messages[2],
                message: messages[0],
                accept: () => {
                  if (!isNullOrUndefined(closingTab)) {
                    this._tabService.closeTab(closingTab.index);
                  }
                },
                reject: () => {
                  this._tabService.setActiveTab(closingTab.index);
                  this._tabService.revertTabChangeEvent.emit();
                },
              });
            });
          }
        }
      }
    );
  }

  contactSearch($event: AutoCompleteCompleteEvent) {
    if ($event.query) {
      this.filteredContacts = this.allContacts
        .filter((c) =>
          (c.firstName + ' ' + c.lastName)
            .toLowerCase()
            .includes($event.query.toLowerCase())
        )
        .filter((c: Contact) => c.inactiveAdAccount === false)
        .map((c) => ({ label: c.firstName + ' ' + c.lastName, value: c.id }));
    } else {
      this.filteredContacts = this.allContacts.map((c) => ({
        label: c.firstName + ' ' + c.lastName,
        value: c.id,
      }));
    }
  }
}
